import React from 'react';
import styled from 'styled-components';
import { FlexRow } from '../../../../shared/FlexRow';
import { Body, Label, Title } from '../../../../shared/styled-components/styled_text';
import { StringUtils } from '../../../../common/utils/StringUtils';

interface Props {
  orgName?: string;
  companyAddress: string;
  orgContact: string;
  style?: React.CSSProperties;
}

export const OnboardingStep1PreviewCompanyInfo: React.FC<Props> = ({ orgName, companyAddress, orgContact, style }) => {
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  const handlePhoneClick = (phoneNumber: string) => {
    if (!isMobile) return;
    window.open(`tel:${phoneNumber}`, '_self');
  };

  return (
    <Container style={style}>
      <Header>
        <DefaultAvatar $letter={orgName?.at(0) ?? '회'} />
        <CompanyName size={'m'} $filled={!!orgName}>
          {!orgName ? '회사 이름' : orgName}
        </CompanyName>
      </Header>
      <FlexRow width={'100%'} gap={'0.8rem'}>
        <Info onClick={() => handlePhoneClick(orgContact)}>
          <FlexRow gap={'0.4rem'} justify={'flex-start'} align={'center'}>
            <Label size={'m'}>📞</Label>
            <Label size={'s'} style={{ color: 'var(--content-color-tertiary)' }}>
              회사 연락처
            </Label>
          </FlexRow>
          <Body size={'s'} style={{ color: 'var(--content-color-primary)', minHeight: '2.4rem' }}>
            {orgContact}
          </Body>
        </Info>
      </FlexRow>
      <Info style={{ marginTop: '0.2rem' }}>
        <FlexRow gap={'0.4rem'} justify={'flex-start'} align={'center'}>
          <Label size={'m'}>🏢</Label>
          <Label size={'s'} style={{ color: 'var(--content-color-tertiary)' }}>
            주소
          </Label>
        </FlexRow>
        <Body
          size={'s'}
          style={{
            maxWidth: '100%',
            color: 'var(--content-color-primary)',
            minHeight: '2.4rem',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'normal',
          }}
        >
          {companyAddress}
        </Body>
      </Info>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  margin-top: 1.2rem;
  box-sizing: border-box;
`;

const Header = styled.div`
  width: 100%;
  min-height: 5rem;
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 0.6rem;

  > img {
    width: 3.2rem;
    height: 3.2rem;
    max-width: 3.2rem;
    aspect-ratio: 1 / 1;
    object-fit: contain;
    flex-shrink: 0;
  }
`;

const Base = styled.div<{ $borderRadius?: number }>`
  min-width: 32px;
  width: 32px;
  min-height: 32px;
  height: 32px;
  border-radius: ${props => props.$borderRadius ?? 6}px;
  flex-shrink: 0;
`;

const DefaultAvatar = styled(Base)<{ $letter: string; $randomColor?: { backgroundColor: string; textColor: string } }>`
  background-color: ${props => props.$randomColor?.backgroundColor ?? 'var(--primary-40)'};

  &:after {
    content: '${props => StringUtils.sanitize(props.$letter)}';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    font-size: 17px;
    color: ${props => props.$randomColor?.textColor ?? 'white'};
  }
`;

const CompanyName = styled(Title)<{ $filled?: boolean }>`
  width: fit-content;
  height: fit-content;
  padding-inline: 0.4rem;
  border-radius: 4px;
  background-color: var(--mono-op-12);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  color: ${props => (props.$filled ? 'var(--content-color-primary)' : 'var(--content-color-tertiary)')};
`;

const Info = styled.div`
  width: 100%;
  padding: 1.2rem;
  background-color: var(--mono-op-08);
  border-radius: 8px;
  box-sizing: border-box;

  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 0.6rem;
  flex-grow: 1;
`;
